import { isEditableFinancialDraft } from '../../../shared/accounting-draft-eligibility';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../../shared/state/pending-action-tracker';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PageShellComponent } from '../../../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../../../shared/page-layout/page-data-panel.component';
import { PageActionPaneComponent } from '../../../../shared/page-layout/page-action-pane.component';
import { FinancePdfViewButtonComponent } from '../../../finance-pdf-view-button.component';
import { AccountsService, FinancePdfStatus } from '../../../accounts.service';
import { Receipt } from '../../../receipt/receipt.model';
import { AccountingDocumentAction, DocumentAuditEvent, GlPreview } from '../../../accounting-lifecycle.model';
import { AccountingTabLabelComponent } from '../../../shared/accounting-tab-label.component';
import { AccountingAuditTimelineComponent } from '../../../shared/accounting-audit-timeline.component';
import { AccountingGlPreviewPanelComponent } from '../../../shared/accounting-gl-preview-panel.component';
import { AccountingAttachmentsPanelComponent } from '../../../shared/accounting-attachments-panel.component';
import { ManualSourceBadgeComponent } from '../../../shared/manual-source-badge.component';
import { CrmPartyContextService } from '../../../../shared/crm-client-contact-picker/crm-party-context.service';
import { AccountingWorkflowActionsComponent } from '../../../shared/accounting-workflow-actions.component';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { PermissionService } from '../../../../security/permission.service';
import { app_permissions } from '../../../../security/app-permissions';

@Component({
    selector: 'app-receipt-detail',
    standalone: true,
    imports: [FinancePdfViewButtonComponent, CommonModule, ButtonModule, ToastModule, TableModule, TabsModule, TagModule, TooltipModule, PageShellComponent, PageDetailHeroComponent, PageDataPanelComponent, PageActionPaneComponent, AccountingTabLabelComponent, AccountingAuditTimelineComponent, AccountingGlPreviewPanelComponent, AccountingAttachmentsPanelComponent, AccountingWorkflowActionsComponent, ManualSourceBadgeComponent],
    template: `
        <app-page-shell>
            <p-toast position="top-center"></p-toast>
            <app-page-detail-hero [auditStamp]="receipt" eyebrow="Customer Receipt" [title]="receipt?.receiptNumber || 'Receipt'"
                [subtitle]="receipt ? crmPartyContext.displayName(receipt) : 'Receipt allocation detail'" icon="pi pi-receipt">
                <div tags>
                    <p-tag [value]="receipt?.allocationStatus || 'UNAPPLIED'" severity="info"></p-tag>
                    <p-tag [value]="receipt?.documentStatus || 'UNKNOWN'" severity="secondary"></p-tag>
                </div>
            </app-page-detail-hero>
            <div class="document-detail-layout" *ngIf="receipt">
                <div class="document-detail-content">
            <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)">
                <p-tablist>
                    <p-tab value="overview"><app-accounting-tab-label icon="pi pi-info-circle" label="Overview"></app-accounting-tab-label></p-tab>
                    <p-tab value="allocations"><app-accounting-tab-label icon="pi pi-link" label="Allocations"></app-accounting-tab-label></p-tab>
                    <p-tab value="journal"><app-accounting-tab-label icon="pi pi-book" label="Journal"></app-accounting-tab-label></p-tab>
                    <p-tab value="attachments"><app-accounting-tab-label icon="pi pi-paperclip" label="Attachments"></app-accounting-tab-label></p-tab>
                    <p-tab value="activity"><app-accounting-tab-label icon="pi pi-history" label="Activity"></app-accounting-tab-label></p-tab>
                    <p-tab value="related"><app-accounting-tab-label icon="pi pi-share-alt" label="Related"></app-accounting-tab-label></p-tab>
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel value="overview"><app-page-data-panel title="Receipt Summary"><div class="detail-grid"><div><strong>Amount</strong><span>{{ money(receipt.amount) }}</span></div><div><strong>Allocated</strong><span>{{ money(receipt.allocatedAmount) }}</span></div><div><strong>Open</strong><span>{{ money(receipt.openAmount) }}</span></div><div><strong>Date</strong><span>{{ receipt.receiptDate | date:'yyyy-MM-dd' }}</span></div></div></app-page-data-panel></p-tabpanel>
                    <p-tabpanel value="allocations"><app-page-data-panel title="Linked invoices"><p-table [value]="receipt.invoices || []"><ng-template pTemplate="header"><tr><th>Invoice</th><th>Amount</th><th>Status</th></tr></ng-template><ng-template pTemplate="body" let-invoice><tr><td>{{ invoice.invoiceNumber }}</td><td>{{ money(invoice.allocatedAmount || invoice.totalAmount) }}</td><td>{{ invoice.allocationStatus || invoice.status }}</td></tr></ng-template></p-table></app-page-data-panel></p-tabpanel>
                    <p-tabpanel value="journal"><app-accounting-gl-preview-panel [preview]="glPreview"></app-accounting-gl-preview-panel></p-tabpanel>
                    <p-tabpanel value="attachments"><app-accounting-attachments-panel documentType="RECEIPT" [documentId]="receipt.receiptId" [attachments]="$any(receipt).attachments || []"></app-accounting-attachments-panel></p-tabpanel>
                    <p-tabpanel value="activity"><app-accounting-audit-timeline [events]="auditEvents"></app-accounting-audit-timeline></p-tabpanel>
                    <p-tabpanel value="related"><app-page-data-panel title="Related"><div class="detail-grid"><div><strong>Source</strong><span><app-manual-source-badge [sourceType]="$any(receipt).sourceType || 'SYSTEM'" [sourceReference]="$any(receipt).sourceReference" [manualReasonCode]="$any(receipt).manualReasonCode"></app-manual-source-badge></span></div><div><strong>Payment transaction</strong><span>{{ receipt.paymentTransactionNumber || '-' }}</span></div></div></app-page-data-panel></p-tabpanel>
                </p-tabpanels>
            </p-tabs>
                </div>
                <app-page-action-pane title="Receipt Actions" subtitle="Access receipt evidence and return to customer receipts.">
                    <div class="action-buttons">
                        <p-button *ngIf="canEditDraft" label="Edit Draft" icon="pi pi-pencil" severity="secondary" [outlined]="true" (click)="editDraft()"></p-button>
                        <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" (click)="back()"></p-button>
                        <app-finance-pdf-view-button kind="receipt" [documentId]="receipt.receiptId" [documentReference]="receipt.receiptNumber" [postingStatus]="receipt.postingStatus" [disabled]="!pdfStatus || pdfStatus.status === 'MISSING' || documentActions.isPending('download')"></app-finance-pdf-view-button>
                        <p-button [label]="pdfStatus?.status === 'ORIGINAL' ? 'Download original PDF' : pdfStatus?.status === 'LEGACY' ? 'Download legacy stored PDF' : pdfStatus?.status === 'MISSING' ? 'Original unavailable' : pdfStatus?.status === 'DRAFT' ? 'Preview draft PDF' : 'Checking PDF'"
                            [icon]="pdfStatus?.status === 'DRAFT' ? 'pi pi-print' : 'pi pi-download'" severity="info" [outlined]="true"
                            [pTooltip]="pdfStatus?.status === 'DRAFT' ? 'Generate a preview from the latest saved changes.' : 'Download the stored receipt PDF.'" [loading]="documentActions.isPending('download')" [disabled]="!pdfStatus"
                            (click)="download()"></p-button>
                    </div>
                    <div class="action-section action-section--primary" *ngIf="workflowActions.length">
                        <span class="action-section-title">Accounting</span>
                        <app-accounting-workflow-actions documentType="RECEIPT" [postedAt]="receipt.postedAt" [documentStatus]="receipt.documentStatus" [postingStatus]="receipt.postingStatus"
                            [allowedActions]="workflowActions" [voidMessage]="voidWarning" [loadingAction]="workflowLoadingAction"
                            (workflow)="workflow($event.action, $event.reason)"></app-accounting-workflow-actions>
                    </div>
                </app-page-action-pane>
            </div>
        </app-page-shell>
    `,
    styles: [`.detail-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:.75rem}.detail-grid div{display:flex;flex-direction:column;gap:.25rem;padding:.75rem;border:1px solid #e2e8f0;border-radius:8px}.document-detail-layout{display:grid;grid-template-columns:minmax(0,1fr) minmax(20rem,22rem);gap:1rem;align-items:start}.document-detail-content{min-width:0}@media (max-width:1080px){.document-detail-layout{grid-template-columns:1fr}}`]
})
export class ReceiptDetailComponent implements OnInit {
    pdfStatus?: FinancePdfStatus;
    readonly documentActions = new PendingActionTracker<string>();
    receipt?: Receipt;
    auditEvents: DocumentAuditEvent[] = [];
    glPreview?: GlPreview;
    activeTab = 'overview';
    canVoid = false;
    workflowLoadingAction: AccountingDocumentAction | null = null;
    readonly voidWarning = 'Voiding this receipt reverses all active invoice applications and reopens the affected invoices. This cannot be undone.';

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private accountsService: AccountsService,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        public crmPartyContext: CrmPartyContextService
    ) {}

    get canEditDraft(): boolean {
        return !!this.receipt?.receiptId && this.permissionService.hasPermission(app_permissions.accounts.receipt.edit)
            && isEditableFinancialDraft(this.receipt, this.sessionStorageService.getCompany()?.id);
    }

    editDraft(): void {
        if (this.canEditDraft) this.router.navigate(['/accounts/enterprise/receivables/generated-receipts/edit', this.receipt!.receiptId]);
    }

    get workflowActions(): AccountingDocumentAction[] {
        const actions: AccountingDocumentAction[] = this.canVoid ? ['void'] : [];
        if (this.permissionService.hasPermission(app_permissions.accounts.receipt.cancel)) actions.push('cancel');
        return actions;
    }

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeTab = this.resolveTab(queryParams.get('tab'));
        });
        this.canVoid = this.permissionService.hasPermission(app_permissions.accounts.receipt.void);
        this.load(id);
    }

    onTabChange(value: string | number) {
        this.activeTab = this.resolveTab(String(value));
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.activeTab },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private resolveTab(tabKey: string | null): string {
        const tabs = ['overview', 'allocations', 'journal', 'attachments', 'activity', 'related'];
        const normalized = (tabKey || 'overview').toLowerCase();
        return tabs.includes(normalized) ? normalized : 'overview';
    }

    money(amount?: number) {
        return Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    }

    workflow(action: AccountingDocumentAction, reason?: string) {
        if (!this.workflowActions.includes(action) || !this.receipt?.receiptId || (action === 'cancel' && !isEditableFinancialDraft(this.receipt))) return;
        this.workflowLoadingAction = action;
        this.accountsService.runReceiptWorkflow(this.receipt.receiptId, action, {
            actorId: this.sessionStorageService.getCustomer()?.id,
            source: 'PORTAL',
            reason
        }).pipe(
            finalize(() => this.workflowLoadingAction = null)
        ).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: action === 'cancel' ? 'Draft cancelled' : 'Receipt voided', detail: 'Receipt applications were rolled back.' });
                this.load(this.receipt!.receiptId!);
            },
            error: err => this.messageService.add({
                severity: 'error',
                summary: 'Receipt action failed',
                detail: err?.error?.message || `The receipt ${action} failed.`
            })
        });
    }

    private load(id: number) {
        this.pdfStatus = undefined;
        this.accountsService.getReceiptById(id).subscribe(receipt => this.receipt = receipt);
        this.accountsService.financePdfStatus('receipt', id).subscribe({ next: status => this.pdfStatus = status,
            error: () => this.pdfStatus = { status: 'MISSING', reprintAvailable: false } });
        this.accountsService.getAuditEvents('RECEIPT', id).subscribe(events => this.auditEvents = events);
        this.accountsService.getGlPreview('RECEIPT', id).subscribe(preview => this.glPreview = preview);
    }

    download() {
        if (!this.receipt?.receiptId || !this.documentActions.begin('download')) return;
        if (this.pdfStatus?.status === 'MISSING') {
            this.documentActions.end('download');
            this.messageService.add({ severity: 'warn', summary: 'Original unavailable', detail: 'No stored issued PDF is available for this receipt.' });
            return;
        }
        this.accountsService.downloadReceiptDocument(this.receipt.receiptId)
            .pipe(finalize(() => this.documentActions.end('download')))
            .subscribe({ next: pdf => this.accountsService.downloadPdfDocument(pdf, 'receipt', this.receipt?.receiptNumber, this.receipt?.receiptId, this.receipt?.postingStatus),
                error: () => this.messageService.add({ severity: 'error', summary: 'Document unavailable', detail: 'The receipt PDF is unavailable.' }) });
    }

    back() {
        this.router.navigate(['/accounts/enterprise/receivables/generated-receipts']);
    }
}
